import React, { useCallback, useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { getUnit, listUnitOccupancies } from '../../../../api/hierarchy-client';
import {
  Occupancy,
  UnitDetail,
  UnitOccupant,
} from '../../../../interfaces/hierarchy-interfaces';
import { BackBar, BackButton } from '../../../layout/back-button';
import { useAuth } from '../../../../context/auth-provider';
import { can, PERMISSIONS } from '../../../../auth/permissions';
import { EntityGallery } from '../../../gallery/entity-gallery';
import { ListSkeleton } from '../dashboard-shared-components/load-state';
import {
  Presence,
  Staleable,
  StateSwitch,
  listItemMotion,
} from '../../../../motion';
import {
  Banner,
  Card,
  CardHeader,
  CardTitle,
  Columns,
  Empty,
  EmptyText,
  EmptyTitle,
  Header,
  HeaderActions,
  HeaderText,
  Muted,
  NoteBlock,
  OccupantIdentity,
  OccupantMeta,
  OccupantName,
  OccupantRow,
  Pill,
  PrimaryButton,
  RowButton,
  Spec,
  SpecGrid,
  SpecLabel,
  SpecValue,
  Stack,
  Sub,
  Tag,
  Timeline,
  TimelineBody,
  TimelineDates,
  TimelineEntry,
  TimelineItem,
  TimelineMarker,
  TimelineName,
  TimelineReason,
  Title,
  Wrap,
} from './dashboard-units.styled';
import {
  MoveInForm,
  MoveOutForm,
  MoveOutTarget,
} from './dashboard-occupancy-forms';
import { OccupantBlock, OccupantPasses, useUnitPasses } from './unit-passes';
import { UnitPromoteModal, UnitStaysCard, UnitStaysHeaderAction, useUnitStays } from './unit-stays-card';
import { staysIsOn, useStaysEntitlement } from '../../../hooks/use-stays-entitlement';
import {
  AreaUnitToggle,
  KindPill,
  OccupancyPill,
  UNIT_STATUS_LABELS,
  formatArea,
  formatBathrooms,
  formatDate,
  occupantFullName,
  readError,
  relationshipLabel,
  useBuildings,
  useMeasurementSystem,
} from './units-shared';

/**
 * UnitDetail — the unit, who is in it now, and everyone who ever was.
 *
 * The history timeline is the reason occupancy is modelled as dated rows rather
 * than a resident column: a move-out closes a record, never deletes it, so
 * "who lived in 4B in March" stays answerable years later. That makes the
 * timeline the primary content of this screen, not an appendix to it.
 */

interface UnitDetailProps {
  // Supplied when the screen is rendered inside another view. Left out, the
  // id comes from the route, so the same component serves both wirings.
  unitId?: number;
}

export const DashboardUnitDetail: React.FC<UnitDetailProps> = ({ unitId }) => {
  const params = useParams<{ unitId?: string; id?: string }>();
  const { permissions } = useAuth();
  const { buildings } = useBuildings();

  const routeId = Number(params.unitId ?? params.id);
  const resolvedId = unitId ?? (Number.isFinite(routeId) ? routeId : NaN);

  const [system, setSystem] = useMeasurementSystem();
  const [unit, setUnit] = useState<UnitDetail | null>(null);
  const [history, setHistory] = useState<Occupancy[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [historyError, setHistoryError] = useState('');
  const [moveInOpen, setMoveInOpen] = useState(false);
  const [moveOutTarget, setMoveOutTarget] = useState<MoveOutTarget | null>(
    null
  );

  const canEditUnit = can(permissions, PERMISSIONS.UNIT_EDIT);
  const canMoveIn = can(permissions, PERMISSIONS.OCCUPANCY_CREATE);
  const canMoveOut = can(permissions, PERMISSIONS.OCCUPANCY_EDIT);
  const canReadOccupancy = can(permissions, PERMISSIONS.OCCUPANCY_READ);
  // Visitor passes (RP-2.10): anyone who can read them sees each resident's
  // live passes; visitorpass:manage (property manager, owner) adds and changes
  // them on the resident's behalf.
  const canReadPasses = can(permissions, PERMISSIONS.VISITORPASS_READ);
  const canManagePasses = can(permissions, PERMISSIONS.VISITORPASS_MANAGE);
  // Lobbero Stays (plan S1, S-4): the header's Promote / "On Stays" and the
  // Stays card — only when the organisation has Stays.
  const canReadStays = can(permissions, PERMISSIONS.STAYS_READ);
  const canPromote = can(permissions, PERMISSIONS.STAYS_PROMOTE);
  const staysOn = staysIsOn(useStaysEntitlement({ enabled: canReadStays }).state);
  const [promoteOpen, setPromoteOpen] = useState(false);

  const load = useCallback(() => {
    if (!Number.isFinite(resolvedId)) {
      setError('That unit link is not valid.');
      setLoading(false);

      return;
    }

    setLoading(true);
    setError('');
    setHistoryError('');

    getUnit(resolvedId)
      .then(setUnit)
      .catch(err => {
        setUnit(null);
        setError(readError(err, 'Could not load this unit.'));
      })
      .finally(() => setLoading(false));

    // The history is a SEPARATE permission (occupancy:read). Requesting it
    // without that permission would 403 and blank a page that otherwise works,
    // so the unit renders regardless of how this call goes.
    if (!canReadOccupancy) {
      setHistory([]);

      return;
    }

    listUnitOccupancies(resolvedId)
      .then(data => setHistory(data.occupancies))
      .catch(err => {
        setHistory([]);
        setHistoryError(readError(err, 'Could not load occupancy history.'));
      });
  }, [resolvedId, canReadOccupancy]);

  useEffect(load, [load]);

  const buildingName =
    buildings.find(b => b.id === unit?.building_id)?.name ?? null;

  const primaryOccupant: UnitOccupant | null =
    unit?.occupants.find(occupant => occupant.is_primary) ?? null;

  // ONE read for the unit; each resident's block filters it to their own
  // occupancy. Not asked for a vacant unit — there is nobody to host a pass.
  const passes = useUnitPasses(
    unit?.building_id,
    unit?.id,
    canReadPasses && !!unit && unit.occupants.length > 0
  );
  const reloadPasses = passes.reload;
  const stays = useUnitStays(unit?.id, staysOn);
  const reloadStays = stays.reload;

  const onSaved = () => {
    setMoveInOpen(false);
    setMoveOutTarget(null);
    load();
    // A move-out ends that resident's passes' LIVE-ness; read them again.
    reloadPasses();
    // A move-in may have changed the unit's listing (the Stays move-in rule).
    reloadStays();
  };

  /* FIRST LOAD: a skeleton the shape of the cards below, in the same box.
     RELOAD (after a move-in or move-out): the page stays and dims. A failure
     with nothing loaded is the banner; the way back is always on screen. */
  const state =
    loading && !unit ? 'loading' : error || !unit ? 'error' : 'ready';

  return (
    <Wrap>
      {/* TOP LEFT, one design, one component (components/layout/back-button).
          It replaced a left-aligned "← Back to units" text link — the third of
          five treatments this product had for the same act. */}
      <BackBar>
        <BackButton to="/dashboard/units" label="Back to units" />
      </BackBar>

      <StateSwitch state={state} minHeight={320}>
      {state === 'loading' ? (
        <ListSkeleton rows={4} label="unit" />
      ) : state === 'error' || !unit ? (
        <Banner tone="alert">{error || 'This unit could not be found.'}</Banner>
      ) : (
      <Staleable stale={loading}>
      <Header>
        <HeaderText>
          {/* A named unit leads with its name; the number moves into the
              subtitle so the identifier is still visible. */}
          <Title>{unit.name || `Unit ${unit.unit_number}`}</Title>
          <Sub>
            {unit.name ? `Unit ${unit.unit_number} · ` : ''}
            {buildingName ? `${buildingName} · ` : ''}
            {unit.floor ? `Floor ${unit.floor}` : 'Floor not recorded'}
          </Sub>
        </HeaderText>
        <HeaderActions>
          <OccupancyPill state={unit} />
          <KindPill kind={unit.kind} />
          {unit.status !== 'active' && (
            <Pill tone="neutral">{UNIT_STATUS_LABELS[unit.status]}</Pill>
          )}
          {canMoveIn && (
            <PrimaryButton type="button" onClick={() => setMoveInOpen(true)}>
              + Move someone in
            </PrimaryButton>
          )}
          {staysOn && (
            <UnitStaysHeaderAction
              state={stays.state}
              canPromote={canPromote}
              onPromote={() => setPromoteOpen(true)}
            />
          )}
        </HeaderActions>
      </Header>

      {unit.kind === 'unassigned' && (
        <Banner tone="warning">
          This unit is a placeholder created for residents who had no unit
          number. Give it a real unit number so the front desk can find them by
          door.
          {canEditUnit && ' Use Edit on the units list to set one.'}
        </Banner>
      )}

      <Columns>
        <Stack>
          <Card>
            {/* The toggle is repeated here, not just on the list: this screen
                is linked to and bookmarked directly, so someone can arrive
                without ever passing the list's copy. */}
            <CardHeader>
              <CardTitle>Unit details</CardTitle>
              <AreaUnitToggle system={system} onChange={setSystem} />
            </CardHeader>
            <SpecGrid>
              <Spec>
                <SpecLabel>Bedrooms</SpecLabel>
                <SpecValue>{unit.bedrooms ?? '—'}</SpecValue>
              </Spec>
              <Spec>
                <SpecLabel>Bathrooms</SpecLabel>
                <SpecValue>{formatBathrooms(unit.bathrooms)}</SpecValue>
              </Spec>
              <Spec>
                <SpecLabel>Size</SpecLabel>
                <SpecValue>{formatArea(unit.sq_ft, system)}</SpecValue>
              </Spec>
              <Spec>
                <SpecLabel>Status</SpecLabel>
                <SpecValue>{UNIT_STATUS_LABELS[unit.status]}</SpecValue>
              </Spec>
            </SpecGrid>
            {unit.notes && <NoteBlock>{unit.notes}</NoteBlock>}
          </Card>

          {/* Lobbero Stays (plan S1): listed or not, and why not. Only when the
              organisation has Stays. */}
          {staysOn && <UnitStaysCard state={stays.state} />}

          {/* The unit's own gallery. Sits under the specs rather than beside
              the occupancy history because a photo is a property OF the unit,
              and the history column is about people. */}
          <Card>
            <EntityGallery
              type="unit"
              entityId={unit.id}
              canEdit={canEditUnit}
            />
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>
                Current occupants
                {unit.occupant_count > 0 ? ` (${unit.occupant_count})` : ''}
              </CardTitle>
            </CardHeader>

            {unit.occupants.length === 0 ? (
              <Empty>
                <EmptyTitle>This unit is vacant</EmptyTitle>
                <EmptyText>
                  Nobody is recorded as living here right now.
                  {canMoveIn ? ' Move someone in to change that.' : ''}
                </EmptyText>
              </Empty>
            ) : (
              unit.occupants.map(occupant => (
                <OccupantBlock key={occupant.id}>
                  <OccupantRow>
                    <OccupantIdentity>
                      <OccupantName>
                        {occupantFullName(occupant)}
                        {occupant.is_primary && <Tag tone="brand">Primary</Tag>}
                        {/* user_id is nullable on purpose — a spouse or minor
                            with no login is still a real resident. */}
                        {occupant.user_id === null && (
                          <Tag tone="neutral">No login</Tag>
                        )}
                      </OccupantName>
                      <OccupantMeta>
                        {relationshipLabel(occupant.relationship)} · since{' '}
                        {formatDate(occupant.moved_in)}
                        {occupant.lease_end
                          ? ` · lease ends ${formatDate(occupant.lease_end)}`
                          : ''}
                      </OccupantMeta>
                    </OccupantIdentity>
                    {canMoveOut && (
                      <RowButton
                        type="button"
                        onClick={() => setMoveOutTarget(occupant)}
                      >
                        Move out
                      </RowButton>
                    )}
                  </OccupantRow>
                  {/* This resident's live visitor passes (RP-2.10). */}
                  {canReadPasses && (
                    <OccupantPasses
                      occupant={occupant}
                      unitLabel={unit.unit_number}
                      buildingId={unit.building_id}
                      passes={passes}
                      canManage={canManagePasses}
                    />
                  )}
                </OccupantBlock>
              ))
            )}
          </Card>
        </Stack>

        <Card>
          <CardHeader>
            <CardTitle>Occupancy history</CardTitle>
            {history.length > 0 && (
              <Muted>
                {history.length} {history.length === 1 ? 'record' : 'records'}
              </Muted>
            )}
          </CardHeader>

          {!canReadOccupancy ? (
            <Empty>
              <EmptyTitle>History not available</EmptyTitle>
              <EmptyText>
                You do not have permission to view occupancy records for this
                unit.
              </EmptyText>
            </Empty>
          ) : historyError ? (
            <Banner tone="alert">{historyError}</Banner>
          ) : history.length === 0 ? (
            <Empty>
              <EmptyTitle>Never occupied</EmptyTitle>
              <EmptyText>
                No one has been recorded in this unit yet. Once someone moves
                in, every stay appears here — including after they leave.
              </EmptyText>
            </Empty>
          ) : (
            <Timeline>
              {history.map((entry, index) => (
                <TimelineItem
                  key={entry.id}
                  {...listItemMotion(index)}
                  layout={false}
                >
                  <TimelineMarker open={entry.is_current} />
                  <TimelineEntry open={entry.is_current}>
                    <TimelineBody>
                      <TimelineName>
                        {occupantFullName(entry)}
                        {entry.is_primary && <Tag tone="brand">Primary</Tag>}
                        {entry.is_current ? (
                          <Tag tone="success">Here now</Tag>
                        ) : (
                          <Tag tone="neutral">Moved out</Tag>
                        )}
                      </TimelineName>
                      <TimelineDates>
                        {formatDate(entry.moved_in)} →{' '}
                        {entry.moved_out
                          ? formatDate(entry.moved_out)
                          : 'present'}
                      </TimelineDates>
                      <OccupantMeta>
                        {relationshipLabel(entry.relationship)}
                        {entry.user_id === null ? ' · no login' : ''}
                      </OccupantMeta>
                      {entry.move_out_reason && (
                        <TimelineReason>
                          Reason: {entry.move_out_reason}
                        </TimelineReason>
                      )}
                    </TimelineBody>
                    {canMoveOut && entry.is_current && (
                      <RowButton
                        type="button"
                        onClick={() => setMoveOutTarget(entry)}
                      >
                        Move out
                      </RowButton>
                    )}
                  </TimelineEntry>
                </TimelineItem>
              ))}
            </Timeline>
          )}
        </Card>
      </Columns>

      {/* Lobbero Stays: the promote form (FormModal animates itself on `open`). */}
      {staysOn && unit ? (
        <UnitPromoteModal
          open={promoteOpen}
          state={stays.state}
          onClose={() => setPromoteOpen(false)}
          onSaved={() => {
            setPromoteOpen(false);
            reloadStays();
          }}
        />
      ) : null}

      {/* Keyed inside a Presence so each dialog animates OUT on close. */}
      <Presence>
        {moveInOpen ? (
          <MoveInForm
            key="move-in"
            unitId={unit.id}
            unitNumber={unit.unit_number}
            currentPrimaryName={
              primaryOccupant ? occupantFullName(primaryOccupant) : null
            }
            canPickUser={can(permissions, PERMISSIONS.USER_READ)}
            today={unit.building_today}
            onClose={() => setMoveInOpen(false)}
            onSaved={onSaved}
          />
        ) : null}
      </Presence>

      <Presence>
        {moveOutTarget ? (
          <MoveOutForm
            key="move-out"
            occupancy={moveOutTarget}
            today={unit.building_today}
            onClose={() => setMoveOutTarget(null)}
            onSaved={onSaved}
          />
        ) : null}
      </Presence>
      </Staleable>
      )}
      </StateSwitch>
    </Wrap>
  );
};
